import {SortDirection} from '@mui/material';
import dayjs from 'dayjs';
import {localized} from 'i18n/i18n';
import {localizedDay, localizedMonth, localizedYear} from 'utilities/platform-helpers/date-helpers';

export const filterListItems = <K extends keyof T, T>(
  items: T[],
  propertyName: K[],
  searchString: string | undefined,
) => {
  if (searchString) {
    return items.filter((item) => {
      // Iterate item properties
      for (let i = 0; i < propertyName.length; i++) {
        const keyValue = item[propertyName[i]];
        const valueString = keyValue ? (keyValue as any).toString() : '';

        if (valueString.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())) {
          return true;
        }
      }
      return false;
    });
  }
  return items;
};

export const sortListItems = <K extends keyof T, T extends Partial<Record<K, string>>>(
  items: T[],
  propertyName: K,
  sortType: SortDirection,
) => {
  const sortedArray = items.concat().sort((a, b) => {
    return ('' + a[propertyName]).localeCompare('' + b[propertyName]);
  });
  if (sortType === 'desc') {
    sortedArray.reverse();
  }

  return sortedArray;
};

export const distinctProperties = <T>(
  data: T[],
  key: keyof T,
  valueFormatter?: (item: T) => string,
): (T[keyof T] | string)[] => {
  if (data.length > 0 && Array.isArray(data[0][key])) {
    return [...new Set(data.flatMap((item) => item[key]))];
  }

  return [...new Set(data.map((item) => (valueFormatter ? valueFormatter(item) : item[key])))];
};

export const isNullOrUndefined = (data: any) => {
  return data === undefined || data === null;
};

export const numericSortWithUndefined = (a: number | undefined, b: number | undefined) => {
  if (isNullOrUndefined(a) && isNullOrUndefined(b)) return 0;
  else if (isNullOrUndefined(a)) return -1;
  else if (isNullOrUndefined(b)) return 1;
  return a! - b!;
};

export const standardValueFormatter = (val: string | number | undefined, appendedString?: string) =>
  !isNullOrUndefined(val) ? [val!.toString(), appendedString].join('') : 'N/A';

export const formatLastSeenDate = (authId?: string, lastSeenEnv?: Date): string => {
  // If authId is not set, user has not accepted the invitation
  if (!authId) return localized('InvitationSent');
  // If authId is set but lastSeen is null, the invitation is accepted but the user has not signed in.
  if (!lastSeenEnv) return localized('InvitationAccepted');

  const now = dayjs();
  const lastSeen = dayjs(lastSeenEnv);

  const days = now.diff(lastSeen, 'd') % 30;
  const months = now.diff(lastSeen, 'M') % 12;
  const years = now.diff(lastSeen, 'y');

  // If same day. i.e after midnight
  if (dayjs(lastSeenEnv).isSame(now, 'd')) return localized('Today');

  // If before midnight but not 24 hours ago
  if (days === 0) return localized('Yesterday');

  let message = '';
  message += years > 0 ? years + ' ' + localizedYear(years, true) + ' ' : '';
  message += months > 0 ? months + ' ' + localizedMonth(months, true) + ' ' : '';
  message += days > 0 ? days + ' ' + localizedDay(days, true) + ' ' : '';
  message += localized('Ago').toLocaleLowerCase();

  return message;
};
